import { Tooltip } from "@material-ui/core";
import { convertUTCToLocalDate } from 'components/UTC/UtcConverter';
import React from 'react';
import CopyIcon from "assets/icons/copy-gray.svg";
import FacebookIcon from "assets/icons/facebook-link.svg";
import InstagramIcon from "assets/icons/instagram-link.svg";
import PreviewIcon from "assets/icons/preview.svg";
import TwitterIcon from "assets/icons/twitter-link.svg";
import { formatDate } from "helper/dateFormat";
import { numberFormat } from "helper/numberFormat";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

const LABEL_STYLE = { fontSize: Fonts.GeneralTinyFont.fontSize, fontWeight: FontWeights.SemiBold, textTransform: "uppercase", letterSpacing: "0.07em", color: FontColors.Slate94A3B8 };
const VALUE_STYLE = { fontSize: Fonts.GeneralLabelFont.fontSize, fontWeight: Fonts.GeneralLabelFont.fontWeight, color: FontColors.Slate0F172A, marginTop: 2 };

const InfoGrid = ({ event }) => (
  <div style={{ display: "flex", background: "rgba(37,85,239,0.04)", borderRadius: 10, border: "1px solid #E9EFFE" }}>
    <div style={{ flex: 1, padding: "14px 16px" }}>
      <div style={LABEL_STYLE}>Event type</div>
      <div style={{ fontSize: Fonts.GeneralSubHeaderFont.fontSize, fontWeight: Fonts.GeneralSubHeaderFont.fontWeight, color: FontColors.Blue2555EF, textTransform: "capitalize", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {event?.eventDetails?.eventType || "—"}
      </div>
    </div>
    <div style={{ width: 1, background: "#E9EFFE", flexShrink: 0 }} />
    <div style={{ flex: 1, padding: "14px 16px" }}>
      <div style={LABEL_STYLE}>{event?.eventDetails?.eventType?.toLowerCase() === "online" ? "Virtual Address" : "Location"}</div>
      <div style={{ fontSize: Fonts.GeneralBodyFont.fontSize, color: FontColors.Slate475569, marginTop: 4 }}>
        {event?.eventDetails?.eventAddress?.formattedAddress || "—"}
      </div>
    </div>
  </div>
);

const EventCalendarItem = ({ event, setCopyLinkSuccess }) => {
  const checkHtmlEmpty = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent?.trim() || div.innerText?.trim() || '';
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/events/${event?.id}`);
      setCopyLinkSuccess(true);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const hasPricing = !!event?.eventPricing?.prices?.length || !!event?.eventDiscount?.length;

  return (
    <div className="event-detail-modal">
      {/* Header */}
      <div className="event-modal-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div className="event-modal-title">{event?.eventName}</div>
            {!event?.displayOnly && (
              <div className="event-modal-reg-ends">
                <span>Registration ends</span>
                <span style={{ fontWeight: FontWeights.Medium, color: FontColors.Slate334155 }}>
                  {event?.alwaysAvailable
                    ? "Always available"
                    : formatDate({ date: event?.eventEndDate, formatType: "defaultWithUTC" })}
                </span>
              </div>
            )}
            {event?.start && (
              <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: Fonts.GeneralInputFont.fontSize, color: FontColors.Slate475569, flexWrap: "wrap" }}>
                <span>📅 {formatDate({ date: event?._utcDate || event?.start, formatType: "defaultWithUTC" })}</span>
                <span>🕑 {formatDate({ date: event?._utcDate || event?.start, formatType: "timeUTC" })}</span>
                {event?.eventDuration?.key && (
                  <span>⏱ {event.eventDuration.key}</span>
                )}
                {event?.eventDetails?.eventType && (
                  <span style={{ background: "#EEF2FF", color: FontColors.Blue2555EF, borderRadius: 20, padding: "1px 10px", fontWeight: Fonts.GeneralSmallLabelFont.fontWeight, fontSize: Fonts.GeneralSmallLabelFont.fontSize, textTransform: "uppercase" }}>
                    {event.eventDetails.eventType}
                  </span>
                )}
              </div>
            )}
            {event?.displayOnly && event?.dates?.length > 1 && (
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                {event.dates.slice(1).map((d, i) => {
                  const start = d?.date || d?.eventStartDate || d;
                  return (
                    <div key={i} style={{
                      display: "flex", flexDirection: "column",
                      background: "#EEF2FF", borderRadius: 10,
                      padding: "8px 14px", minWidth: 0,
                    }}>
                      <span style={{ fontSize: Fonts.GeneralSmallLabelFont.fontSize, fontWeight: Fonts.GeneralSmallLabelFont.fontWeight, color: FontColors.Blue2555EF }}>
                        📅 {formatDate({ date: start, formatType: "defaultWithUTC" })}
                      </span>
                      <span style={{ fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.Slate475569, marginTop: 2 }}>
                        🕑 {formatDate({ date: start, formatType: "timeUTC" })}
                        {event?.eventDuration?.key && ` · ${event.eventDuration.key}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {!event?.displayOnly && (
            <Tooltip title="Copy event link">
              <button className="event-modal-copy-btn" onClick={copyToClipboard} aria-label="Copy event link">
                <img src={CopyIcon} alt="Copy" />
              </button>
            </Tooltip>
          )}
        </div>
        {!event?.displayOnly && (
          <button
            className="event-modal-register-btn"
            onClick={() => window.open(`/events/${event?.id}`, "_blank")}
          >
            <img src={PreviewIcon} alt="" aria-hidden="true" />
            Event Registration
          </button>
        )}
      </div>

      {/* Body */}
      <div className="event-modal-body">
        {/* Info grid: type | date/location */}
        <InfoGrid event={event} />

        {/* Description */}
        {checkHtmlEmpty(event?.description) && (
          <div>
            <div className="event-modal-section-label">Description</div>
            <div
              className="event-modal-desc"
              dangerouslySetInnerHTML={{ __html: event?.description }}
            />
          </div>
        )}

        {/* Contact */}
        {(event?.eventDetails?.contactInfo?.firstName || (event?.eventDetails?.contactInfo?.showEmail && event?.eventDetails?.contactInfo?.email)) && (
          <div>
            <div className="event-modal-section-label">Contact Info</div>
            <div className="event-modal-contact">
              <div className="event-modal-contact-items">
                {(event?.eventDetails?.contactInfo?.firstName || event?.eventDetails?.contactInfo?.lastName) && (
                  <div className="event-modal-contact-item">
                    <span style={{ fontSize: Fonts.GeneralTinyFont.fontSize, fontWeight: FontWeights.SemiBold, textTransform: "uppercase", letterSpacing: "0.07em", color: FontColors.Slate94A3B8 }}>Name</span>
                    <span className="event-modal-contact-value">
                      {event?.eventDetails?.contactInfo?.firstName} {event?.eventDetails?.contactInfo?.lastName}
                    </span>
                  </div>
                )}
                {event?.eventDetails?.contactInfo?.showEmail && event?.eventDetails?.contactInfo?.email && (
                  <div className="event-modal-contact-item">
                    <span style={{ fontSize: Fonts.GeneralTinyFont.fontSize, fontWeight: FontWeights.SemiBold, textTransform: "uppercase", letterSpacing: "0.07em", color: FontColors.Slate94A3B8 }}>Email</span>
                    <span className="event-modal-contact-value">{event?.eventDetails?.contactInfo?.email}</span>
                  </div>
                )}
              </div>
              <div className="event-modal-social-links">
                {event?.eventDetails?.contactInfo?.facebook && (
                  <a href={event.eventDetails.contactInfo.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">
                    <img src={FacebookIcon} alt="Facebook" />
                  </a>
                )}
                {event?.eventDetails?.contactInfo?.instagram && (
                  <a href={event.eventDetails.contactInfo.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
                    <img src={InstagramIcon} alt="Instagram" />
                  </a>
                )}
                {event?.eventDetails?.contactInfo?.twitter && (
                  <a href={event.eventDetails.contactInfo.twitter} target="_blank" rel="noreferrer" aria-label="Twitter">
                    <img src={TwitterIcon} alt="Twitter" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pricing */}
        {hasPricing && (
          <div>
            <div className="event-modal-section-label">Pricing</div>
            <div className="event-modal-pricing">
              {event?.eventPricing?.prices?.map((item, index) =>
                (item?.type === "onRegistration" || item?.type === "optional") ? (
                  <div className="pricing-row" key={index}>
                    <span>
                      {item?.title}
                      {item?.type === "optional" && (
                        <span style={{ fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.Slate94A3B8, marginLeft: 6 }}>(Optional)</span>
                      )}
                    </span>
                    <span className="pricing-amount">
                      {item?.variant && item?.variantAmountRange
                        ? item.variantAmountRange
                        : numberFormat({ number: +item.amount, decimal: 2, style: "currency" })}
                    </span>
                  </div>
                ) : null
              )}
              {event?.eventDiscount?.map((item, index) => (
                <div className="pricing-row" key={`discount-${index}`}>
                  <span>
                    {item?.discount_name}
                    <span style={{ fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.Slate94A3B8, marginLeft: 6 }}>
                      {formatDate({ date: convertUTCToLocalDate(item?.start_date), formatType: "defaultWithUTC" })}
                      {" – "}
                      {formatDate({ date: convertUTCToLocalDate(item?.end_date), formatType: "defaultWithUTC" })}
                    </span>
                  </span>
                  <span className="pricing-amount">
                    {item?.price_type === 'percentage'
                      ? `${item?.value}%`
                      : numberFormat({ number: +item?.value, decimal: 2, style: "currency" })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCalendarItem;
